import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DragAndDrop } from '@components/drag-and-drop';
import { FILE_TYPE_AVIF, FILE_TYPE_JPEG, FILE_TYPE_JPG, FILE_TYPE_PNG, FILE_TYPE_WEBP } from '@constants/global';
import classNames from 'classnames';

import { ImageCropWithDescriptionProps } from './image-crop-with-description.type';

export const ImageCropWithDescription = ({
  id,
  name,
  title,
  subtitle,
  isImgEditable,
  isHorizontal,
  isResponsive,
  setProfileImage,
}: ImageCropWithDescriptionProps) => {
  const { t } = useTranslation();
  const methods = useFormContext();

  const translations = useMemo(() => {
    return {
      title: t('eventDetails:photoTitleEvent'),
      subtitle: t('eventDetails:photoSubtitle'),
      save: t('eventDetails:photoSave'),
      cancel: t('eventDetails:photoCancel'),
      zoom: t('eventDetails:photoZoom'),
      rotate: t('eventDetails:photoRotate'),
    };
  }, [t]);

  return (
    <div
      className={classNames('flex items-center mx-auto', {
        'flex-row gap-4 justify-start text-left': isHorizontal && !isResponsive,
        'flex-col gap-y-3 text-center': !isHorizontal && !isResponsive,
        'flex-col gap-y-3 text-center sm:flex-row sm:gap-6 sm:justify-start sm:text-left': isResponsive,
      })}>
      <div
        className={classNames(' overflow-hidden', {
          'w-[140px] h-[140px]': isHorizontal && !isResponsive,
          'w-[100px] h-[100px]': !isHorizontal && !isResponsive,
          'w-[140px] sm:w-[100px] h-[140px] sm:h-[100px]': isResponsive,
        })}>
        <Controller
          name={name}
          control={methods.control}
          render={({ field }) => {
            return (
              <DragAndDrop
                {...field}
                disabled={!isImgEditable}
                onChange={(value) => {
                  const { file, src } = value[0] || {};
                  setProfileImage?.(file);
                  field.onChange(src);
                }}
                alt='Event Banner'
                id={id}
                name={name}
                allowedTypes={[FILE_TYPE_JPG, FILE_TYPE_JPEG, FILE_TYPE_PNG, FILE_TYPE_WEBP, FILE_TYPE_AVIF]}
                translations={translations}
                disableRemoveImage={true}
                imageFormat={FILE_TYPE_WEBP}
                hideHelper={true}
                buttonEditClassName='bg-primary p-1'
                absolutePositionButtonChangeImageColorClassName='absolute bottom-0 right-0'
                dragAndDropHeight='h-[100px]'
                iconSizeClassName='size-2'
              />
            );
          }}
        />
      </div>
      <div className='flex flex-col gap-y-1'>
        <h1 className='text-primary text-headline-m'>{title}</h1>
        <h2 className='text-tertiary text-body-m'>{subtitle}</h2>
      </div>
    </div>
  );
};
